import { EffectComposer } from "postprocessing";

const originalRender = EffectComposer.prototype.render;
const nativeRequestAnimationFrame = window.requestAnimationFrame.bind(window);
const nativeCancelAnimationFrame = window.cancelAnimationFrame.bind(window);

let hyperspeedActive = true;
let pendingHyperspeedTick = null;
let syntheticFrameId = -1;
const hyperspeedTicks = new WeakSet();

if (!window.__carthyHyperspeedRafManaged) {
  window.__carthyHyperspeedRafManaged = true;

  window.requestAnimationFrame = (callback) => {
    const hyperspeedCanvasExists = Boolean(document.querySelector(".hyperspeed canvas"));
    const looksLikeHyperspeedTick =
      typeof callback === "function" &&
      callback.name === "bound tick" &&
      hyperspeedCanvasExists;

    if (looksLikeHyperspeedTick) hyperspeedTicks.add(callback);

    if (hyperspeedTicks.has(callback) && !hyperspeedActive) {
      pendingHyperspeedTick = callback;
      syntheticFrameId -= 1;
      return syntheticFrameId;
    }

    return nativeRequestAnimationFrame(callback);
  };

  window.cancelAnimationFrame = (frameId) => {
    if (frameId < 0) {
      pendingHyperspeedTick = null;
      return;
    }
    nativeCancelAnimationFrame(frameId);
  };
}

if (!EffectComposer.prototype.__carthyHyperspeedManaged) {
  EffectComposer.prototype.__carthyHyperspeedManaged = true;

  EffectComposer.prototype.render = function carthyManagedRender(...args) {
    const canvas = this.renderer?.domElement;
    const hyperspeed = canvas?.closest?.(".hyperspeed");

    if (hyperspeed?.dataset.carthyPerformanceState?.startsWith("paused-")) {
      return undefined;
    }

    return originalRender.apply(this, args);
  };
}

function resumePendingTick() {
  if (!hyperspeedActive || !pendingHyperspeedTick) return;
  const callback = pendingHyperspeedTick;
  pendingHyperspeedTick = null;
  nativeRequestAnimationFrame(callback);
}

function setState(hyperspeed, active, reason = "offscreen") {
  if (!hyperspeed) return;

  hyperspeedActive = active;
  hyperspeed.dataset.carthyPerformance = "hyperspeed-webgl";
  hyperspeed.dataset.carthyPerformanceState = active ? "active" : `paused-${reason}`;
  hyperspeed.dataset.carthyRafState = active ? "running" : "suspended";

  document.documentElement.dataset.carthyHyperspeedPerformance = active ? "active" : `paused-${reason}`;
  document.documentElement.dataset.carthyHyperspeedRaf = active ? "running" : "suspended";

  if (active) resumePendingTick();
}

function installHyperspeedManager() {
  const hyperspeed = document.querySelector(".hyperspeed");
  const hero = document.querySelector("#top");
  if (!hyperspeed || !hero) return false;
  if (hyperspeed.dataset.carthyPerformanceManager === "installed") return true;

  hyperspeed.dataset.carthyPerformanceManager = "installed";
  document.documentElement.dataset.carthyHyperspeedManager = "enabled";

  let visible = true;

  const applyVisibility = () => {
    setState(
      hyperspeed,
      visible && !document.hidden,
      document.hidden ? "hidden" : "offscreen",
    );
  };

  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      visible = Boolean(entry?.isIntersecting && entry.intersectionRatio > 0.01);
      applyVisibility();
    },
    {
      threshold: [0, 0.01, 0.1],
      rootMargin: "80px 0px 80px 0px",
    },
  );

  observer.observe(hero);
  document.addEventListener("visibilitychange", applyVisibility);
  applyVisibility();
  return true;
}

function boot() {
  if (installHyperspeedManager()) return;

  const observer = new MutationObserver(() => {
    if (installHyperspeedManager()) observer.disconnect();
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot, { once: true });
} else {
  boot();
}
